import { useCallback, useEffect, useRef, useState } from 'react';
import EasternGalileeMapPicker from '@/components/map/EasternGalileeMapPicker';
import { fetchSettlements } from '@/lib/api/agritour';
import type { ParcelIdentifyResult } from '@/lib/api/map';
import {
  formatItm,
  ITM_VALIDATION_MESSAGES,
  itmToWgs84,
  resolveItmFromForm,
  validateItmPair,
  wgs84ToItm,
} from '@/lib/map/itm';
import { isWithinEasternGalilee } from '@/lib/map/eastern-galilee';
import {
  AGRITOUR_SECTIONS,
  OWNERSHIP_OPTIONS,
  PARCEL_TYPE_OPTIONS,
  showExistingBuildingPermitField,
  STATUTORY_MATURITY_OPTIONS,
  YES_NO,
} from './agritour-form-copy';
import AgriTourSelect from './AgriTourSelect';
import type { AgriTourFormData, AgriTourFiles } from './AgriTourScreen';
import styles from './AgriTourScreen.module.css';

interface AgriTourStepBProps {
  formData: AgriTourFormData;
  files: AgriTourFiles;
  fieldErrors: Record<string, string>;
  onChange: (patch: Partial<AgriTourFormData>) => void;
  onFileChange: (key: keyof AgriTourFiles, file: File | null) => void;
}

function parseStoredGeometry(
  polygonJson: string,
): { type: string; coordinates: unknown } | null {
  if (!polygonJson.trim()) return null;
  try {
    return JSON.parse(polygonJson) as { type: string; coordinates: unknown };
  } catch {
    return null;
  }
}

function hasValidPointSelection(latStr: string, lngStr: string): boolean {
  if (!latStr.trim() || !lngStr.trim()) return false;
  const lat = Number(latStr);
  const lng = Number(lngStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return isWithinEasternGalilee(lat, lng);
}

export default function AgriTourStepB({
  formData,
  files,
  fieldErrors,
  onChange,
  onFileChange,
}: AgriTourStepBProps) {
  const [parcelGeometry, setParcelGeometry] = useState<{
    type: string;
    coordinates: unknown;
  } | null>(() => parseStoredGeometry(formData.polygonJson));
  const [selectionMode, setSelectionMode] = useState<'point' | 'polygon_drawn'>(() =>
    formData.selectionMode === 'polygon_drawn' ? 'polygon_drawn' : 'point',
  );
  const [identifyError, setIdentifyError] = useState<string | undefined>();
  const [settlements, setSettlements] = useState<
    { id: string; name: string; lat?: number; lng?: number }[]
  >([]);
  const itmSyncedRef = useRef(false);

  useEffect(() => {
    fetchSettlements()
      .then(setSettlements)
      .catch(() => setSettlements([]));
  }, []);

  const focusSettlement = (() => {
    if (!formData.settlementItemId) return null;
    const match = settlements.find((s) => s.id === formData.settlementItemId);
    if (!match || match.lat == null || match.lng == null) return null;
    return { lat: match.lat, lng: match.lng };
  })();

  useEffect(() => {
    setParcelGeometry(parseStoredGeometry(formData.polygonJson));
    if (formData.selectionMode === 'polygon_drawn' || formData.selectionMode === 'point') {
      setSelectionMode(formData.selectionMode);
    }
  }, [formData.polygonJson, formData.selectionMode]);

  useEffect(() => {
    if (!hasValidPointSelection(formData.lat, formData.lng)) return;
    if (itmSyncedRef.current) return;
    const resolved = resolveItmFromForm(
      formData.lat,
      formData.lng,
      formData.coordX,
      formData.coordY,
    );
    if (!resolved) return;
    if (
      formData.coordX === String(resolved.easting) &&
      formData.coordY === String(resolved.northing)
    ) {
      itmSyncedRef.current = true;
      return;
    }
    itmSyncedRef.current = true;
    onChange({
      coordX: String(resolved.easting),
      coordY: String(resolved.northing),
    });
  }, [formData.lat, formData.lng, formData.coordX, formData.coordY, onChange]);

  useEffect(() => {
    if (hasValidPointSelection(formData.lat, formData.lng)) return;
    if (!formData.coordX.trim() && !formData.coordY.trim()) return;
    onChange({ coordX: '', coordY: '' });
  }, [formData.lat, formData.lng, formData.coordX, formData.coordY, onChange]);

  const handleSelectionModeChange = useCallback(
    (mode: 'point' | 'polygon_drawn') => {
      setSelectionMode(mode);
      onChange({
        selectionMode: mode,
        ...(mode === 'polygon_drawn' ? { coordX: '', coordY: '' } : {}),
      });
    },
    [onChange],
  );

  const handleParcelIdentified = useCallback(
    (result: ParcelIdentifyResult) => {
      setIdentifyError(undefined);
      const geometry = result.geometry ?? null;
      setParcelGeometry(geometry);
      const mode = result.selection_mode ?? selectionMode;
      const itm = wgs84ToItm(result.lng, result.lat);
      const formatted = mode === 'point' ? formatItm(itm.easting, itm.northing) : null;
      onChange({
        lat: String(result.lat),
        lng: String(result.lng),
        selectionMode: mode,
        polygonJson: geometry ? JSON.stringify(geometry) : '',
        coordX: formatted ? String(formatted.easting) : '',
        coordY: formatted ? String(formatted.northing) : '',
      });
    },
    [onChange, selectionMode],
  );

  const applyItmToMap = useCallback(
    (eastingRaw: string, northingRaw: string) => {
      const easting = Number(eastingRaw);
      const northing = Number(northingRaw);
      if (!eastingRaw.trim() || !northingRaw.trim()) return;

      const code = validateItmPair(easting, northing);
      if (code) return;

      const { lng, lat } = itmToWgs84(easting, northing);
      const rounded = formatItm(easting, northing);
      setParcelGeometry(null);
      setSelectionMode('point');
      onChange({
        coordX: String(rounded.easting),
        coordY: String(rounded.northing),
        lat: String(lat),
        lng: String(lng),
        selectionMode: 'point',
        polygonJson: '',
      });
    },
    [onChange],
  );

  const handleItmBlur = useCallback(() => {
    if (selectionMode === 'polygon_drawn') return;
    applyItmToMap(formData.coordX, formData.coordY);
  }, [applyItmToMap, formData.coordX, formData.coordY, selectionMode]);

  const handleItmFieldChange = useCallback(
    (field: 'coordX' | 'coordY', value: string) => {
      if (
        selectionMode === 'polygon_drawn' &&
        parcelGeometry &&
        value.trim()
      ) {
        const confirmed = window.confirm(AGRITOUR_SECTIONS.b.itmPolygonConfirm);
        if (!confirmed) return;
        setParcelGeometry(null);
        setSelectionMode('point');
        onChange({ selectionMode: 'point', polygonJson: '' });
      }
      onChange({ [field]: value });
    },
    [onChange, parcelGeometry, selectionMode],
  );

  const lat = formData.lat ? Number(formData.lat) : undefined;
  const lng = formData.lng ? Number(formData.lng) : undefined;
  const pointSelected = hasValidPointSelection(formData.lat, formData.lng);
  const showItmFields = selectionMode === 'point' && pointSelected;

  const handleFileChange = (key: keyof AgriTourFiles, file: File | null) => {
    onFileChange(key, file);
  };

  const itmInlineError = (() => {
    if (!showItmFields) return undefined;
    if (!formData.coordX.trim() && !formData.coordY.trim()) return undefined;
    const code = validateItmPair(Number(formData.coordX), Number(formData.coordY));
    return code ? ITM_VALIDATION_MESSAGES[code] : undefined;
  })();

  return (
    <section aria-label={AGRITOUR_SECTIONS.b.title}>

      <h3 className={styles.sectionSubhead}>{AGRITOUR_SECTIONS.b.coordinates}</h3>
      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          סמן את מיקום המיזם על המפה (נקודה או פוליגון)
        </label>
        <p className={styles.mapNote}>
          ניתן לסמן נקודה על ידי לחיצה על המפה, או לצייר פוליגון באמצעות כלי השרטוט
        </p>
        <div className={styles.mapWrapper}>
          <EasternGalileeMapPicker
            lat={lat}
            lng={lng}
            parcelGeometry={parcelGeometry}
            selectionMode={selectionMode}
            onSelectionModeChange={handleSelectionModeChange}
            onParcelIdentified={handleParcelIdentified}
            onIdentifyError={(msg) => setIdentifyError(msg)}
            identifyError={identifyError}
            mapAriaLabel="מפה לבחירת מיקום המיזם"
            layersEnabled={false}
            defaultBasemap="aerial"
            showBasemapToggle
            settlements={settlements}
            focusSettlement={focusSettlement}
            selectedSettlementId={formData.settlementItemId}
          />
        </div>
      </div>

      {selectionMode === 'polygon_drawn' ? (
        <p className={styles.fieldHint}>{AGRITOUR_SECTIONS.b.itmPolygonModeNote}</p>
      ) : showItmFields ? (
        <div className={styles.field}>
          <p className={styles.fieldHint}>{AGRITOUR_SECTIONS.b.itmHint}</p>
          <div className={styles.itmRow}>
            <div className={styles.itmField}>
              <label className={styles.fieldLabel} htmlFor="agritour-itm-easting">
                {AGRITOUR_SECTIONS.b.itmEasting}
              </label>
              <input
                id="agritour-itm-easting"
                type="text"
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                className={`${styles.input} ${fieldErrors.coordX || itmInlineError ? styles.inputError : ''}`}
                value={formData.coordX}
                onChange={(e) => handleItmFieldChange('coordX', e.target.value)}
                onBlur={handleItmBlur}
                aria-invalid={Boolean(fieldErrors.coordX || itmInlineError)}
              />
            </div>
            <div className={styles.itmField}>
              <label className={styles.fieldLabel} htmlFor="agritour-itm-northing">
                {AGRITOUR_SECTIONS.b.itmNorthing}
              </label>
              <input
                id="agritour-itm-northing"
                type="text"
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                className={`${styles.input} ${fieldErrors.coordY || itmInlineError ? styles.inputError : ''}`}
                value={formData.coordY}
                onChange={(e) => handleItmFieldChange('coordY', e.target.value)}
                onBlur={handleItmBlur}
                aria-invalid={Boolean(fieldErrors.coordY || itmInlineError)}
              />
            </div>
          </div>
          {(fieldErrors.coordX || fieldErrors.coordY || itmInlineError) && (
            <p className={styles.fieldError} role="alert">
              {fieldErrors.coordX || fieldErrors.coordY || itmInlineError}
            </p>
          )}
        </div>
      ) : null}

      <h3 className={styles.sectionSubhead}>{AGRITOUR_SECTIONS.b.parcelIds}</h3>

      {/* גוש */}
      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`} htmlFor="agritour-gush">
          מספר גוש
        </label>
        <input
          id="agritour-gush"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={`${styles.input} ${fieldErrors.gushNumber ? styles.inputError : ''}`}
          value={formData.gushNumber}
          onChange={(e) => onChange({ gushNumber: e.target.value.replace(/\D/g, '') })}
          style={{ maxWidth: 160 }}
        />
        {fieldErrors.gushNumber && <p className={styles.fieldError} role="alert">{fieldErrors.gushNumber}</p>}
      </div>

      {/* חלקה */}
      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`} htmlFor="agritour-helka">
          מספר חלקה/חלקות
        </label>
        <input
          id="agritour-helka"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={`${styles.input} ${fieldErrors.helkaNumber ? styles.inputError : ''}`}
          value={formData.helkaNumber}
          onChange={(e) => onChange({ helkaNumber: e.target.value.replace(/\D/g, '') })}
          style={{ maxWidth: 160 }}
        />
        {fieldErrors.helkaNumber && <p className={styles.fieldError} role="alert">{fieldErrors.helkaNumber}</p>}
      </div>

      {/* משק */}
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="agritour-meshek">
          מספר משק (במידה ורלוונטי)
        </label>
        <input
          id="agritour-meshek"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={styles.input}
          value={formData.meshekNumber}
          onChange={(e) => onChange({ meshekNumber: e.target.value.replace(/\D/g, '') })}
          style={{ maxWidth: 160 }}
        />
      </div>

      {/* בעלות */}
      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          סטטוס בעלות/זכויות בקרקע
        </label>
        <p className={styles.fieldHint}>אנא בחר את סטטוס הזכויות בקרקע מהרשימה למטה.</p>
        <AgriTourSelect
          value={formData.ownershipStatus}
          options={OWNERSHIP_OPTIONS}
          onChange={(v) => onChange({ ownershipStatus: v })}
          hasError={Boolean(fieldErrors.ownershipStatus)}
        />
      </div>

      {formData.ownershipStatus === '1' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            סטטוס בעלות/זכויות בקרקע - אחר
          </label>
          <p className={styles.fieldHint}>יש לפרט</p>
          <input
            type="text"
            className={styles.input}
            value={formData.ownershipStatusOther}
            onChange={(e) => onChange({ ownershipStatusOther: e.target.value })}
          />
        </div>
      )}

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          סוג החלקה במשבצת החקלאית של היישוב
        </label>
        <p className={styles.fieldHint}>אנא בחר את סוג החלקה המתאים ליישוב שלך מתוך הרשימה.</p>
        <AgriTourSelect
          value={formData.parcelType}
          options={PARCEL_TYPE_OPTIONS}
          onChange={(v) => onChange({ parcelType: v })}
          hasError={Boolean(fieldErrors.parcelType)}
        />
      </div>

      <h3 className={styles.sectionSubhead}>{AGRITOUR_SECTIONS.b.landDocs}</h3>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          האם הסכם חכירה/חוזה משבצת מול רשות מקרקעי ישראל (רמ״י) ברשותך?
        </label>
        <AgriTourSelect
          value={formData.leaseAgreementRMI}
          options={YES_NO}
          onChange={(v) => onChange({ leaseAgreementRMI: v })}
          hasError={Boolean(fieldErrors.leaseAgreementRMI)}
        />
      </div>
      {formData.leaseAgreementRMI === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            הסכם חכירה/חוזה משבצת מול רשות מקרקעי ישראל (רמ"י)
          </label>
          <p className={styles.fieldHint}>יש לצרף את הסכם זה</p>
          {fieldErrors.filev6ow2t9a && (
            <p className={styles.fieldError} role="alert">{fieldErrors.filev6ow2t9a}</p>
          )}
          <div className={styles.fileRow}>
            <label className={styles.fileButton}>
              בחר קובץ
              <input
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('filev6ow2t9a', e.target.files?.[0] ?? null)}
              />
            </label>
            {files.filev6ow2t9a && (
              <span className={styles.fileName}>{files.filev6ow2t9a.name}</span>
            )}
          </div>
        </div>
      )}

      {/* הסכם שכירות */}
      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם הסכם הסכם שכירות/בר-רשות מול האגודה החקלאית/היישוב ברשותך?
        </label>
        <AgriTourSelect
          value={formData.rentalAgreementVillage}
          options={YES_NO}
          onChange={(v) => onChange({ rentalAgreementVillage: v })}
          hasError={Boolean(fieldErrors.rentalAgreementVillage)}
        />
      </div>
      {formData.rentalAgreementVillage === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            הסכם שכירות/בר-רשות מול האגודה החקלאית/היישוב
          </label>
          <p className={styles.fieldHint}>יש לצרף את הסכם זה</p>
          {fieldErrors.filemk54lt2a && (
            <p className={styles.fieldError} role="alert">{fieldErrors.filemk54lt2a}</p>
          )}
          <div className={styles.fileRow}>
            <label className={styles.fileButton}>
              בחר קובץ
              <input
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('filemk54lt2a', e.target.files?.[0] ?? null)}
              />
            </label>
            {files.filemk54lt2a && (
              <span className={styles.fileName}>{files.filemk54lt2a.name}</span>
            )}
          </div>
        </div>
      )}

      {/* הסכם 5 שנים */}
      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          במידה והיזם אינו בעל הזכויות ישירות: האם קיים הסכם חתום עם בעל הקרקע לתקופה של לפחות 5 שנים קדימה ממועד זה?
        </label>
        <p className={styles.fieldHint}>
          <strong>אם היזם הוא בעל הזכויות הישירות, יש לסמן לא</strong>
        </p>
        <AgriTourSelect
          value={formData.fiveYearAgreement}
          options={YES_NO}
          onChange={(v) => onChange({ fiveYearAgreement: v })}
          hasError={Boolean(fieldErrors.fiveYearAgreement)}
        />
      </div>
      {formData.fiveYearAgreement === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            הסכם חתום עם בעל הקרקע לתקופה של לפחות 5 שנים קדימה
          </label>
          <p className={styles.fieldHint}>יש לצרף את הסכם זה</p>
          {fieldErrors.file4khnuexa && (
            <p className={styles.fieldError} role="alert">{fieldErrors.file4khnuexa}</p>
          )}
          <div className={styles.fileRow}>
            <label className={styles.fileButton}>
              בחר קובץ
              <input
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('file4khnuexa', e.target.files?.[0] ?? null)}
              />
            </label>
            {files.file4khnuexa && (
              <span className={styles.fileName}>{files.file4khnuexa.name}</span>
            )}
          </div>
        </div>
      )}

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          בשלות סטטוטורית של המיזם
        </label>
        <p className={styles.fieldHint}>
          סמן את הסטטוס המדויק ביותר — <em>משפיע ישירות על ניקוד הבקשה</em>
        </p>
        <AgriTourSelect
          value={formData.statutoryMaturity}
          options={STATUTORY_MATURITY_OPTIONS}
          onChange={(v) => {
            onChange({ statutoryMaturity: v });
            if (!showExistingBuildingPermitField(v)) {
              onFileChange('file_mm4m76fa', null);
            }
          }}
          hasError={Boolean(fieldErrors.statutoryMaturity)}
        />
      </div>
      {formData.statutoryMaturity === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>פטור המיזם מהיתר בנייה</label>
          <p className={styles.fieldHint}>יש להציג אישור מוסמך על פטור</p>
          {fieldErrors.fileki8tq148 && (
            <p className={styles.fieldError} role="alert">{fieldErrors.fileki8tq148}</p>
          )}
          <div className={styles.fileRow}>
            <label className={styles.fileButton}>
              בחר קובץ
              <input
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('fileki8tq148', e.target.files?.[0] ?? null)}
              />
            </label>
            {files.fileki8tq148 && (
              <span className={styles.fileName}>{files.fileki8tq148.name}</span>
            )}
          </div>
        </div>
      )}

      {showExistingBuildingPermitField(formData.statutoryMaturity) && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={styles.fieldLabel}>היתר בנייה קיים</label>
          <div className={styles.fileRow}>
            <label className={styles.fileButton}>
              בחר קובץ
              <input
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange('file_mm4m76fa', e.target.files?.[0] ?? null)}
              />
            </label>
            {files.file_mm4m76fa && (
              <span className={styles.fileName}>{files.file_mm4m76fa.name}</span>
            )}
          </div>
        </div>
      )}

      {/* תוכניות מקודמות בסביבה */}
      <div className={styles.field}>
        <label className={styles.fieldLabel}>תוכניות מקודמות בסביבה</label>
        <p className={styles.fieldHint}>
          האם ידוע לך על תוכניות בניין עיר (תב&quot;ע) מקומיות או מחוזיות המקודמות במקום או בסמוך אליו
          (למשל: הרחבת יישוב, תוכנית דרכים, תוכנית סביבתית, שינוי ייעודי קרקע)?
        </p>
        <AgriTourSelect
          value={formData.nearbyPlans}
          options={YES_NO}
          onChange={(v) => onChange({ nearbyPlans: v })}
          hasError={Boolean(fieldErrors.nearbyPlans)}
        />
      </div>
      {formData.nearbyPlans === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            פירוט תוכניות מקודמות בסביבה
          </label>
          <p className={styles.fieldHint}>יש לפרט</p>
          <textarea
            className={styles.textarea}
            value={formData.environmentalPlansDetail}
            onChange={(e) => onChange({ environmentalPlansDetail: e.target.value })}
            rows={3}
          />
        </div>
      )}

      {/* עוגנים תיירותיים */}
      <div className={styles.field}>
        <label className={styles.fieldLabel}>עוגנים תיירותיים נוספים הקיימים בסביבה</label>
        <p className={styles.fieldHint}>
          האם קיימים בסביבת המיזם (ביישוב או במרחב הסמוך) עוגנים תיירותיים, אטרקציות, מוקדי משיכה
          או מסלולי טיול קיימים שהמיזם יכול להישען עליהם, להשתלב בהם או לייצר איתם שיתופי פעולה?
        </p>
        <AgriTourSelect
          value={formData.touristAnchors}
          options={YES_NO}
          onChange={(v) => onChange({ touristAnchors: v })}
          hasError={Boolean(fieldErrors.touristAnchors)}
        />
      </div>
      {formData.touristAnchors === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            פירוט עוגנים תיירותים נוספים הקיימים בסביבה
          </label>
          <p className={styles.fieldHint}>פרט אילו עוגנים קיימים וכיצד המיזם משתלב איתם</p>
          <textarea
            className={styles.textarea}
            value={formData.touristAnchorsDetail}
            onChange={(e) => onChange({ touristAnchorsDetail: e.target.value })}
            rows={3}
          />
        </div>
      )}
    </section>
  );
}
